import { _ } from "../../../polyglot/polyglot.js";
import { validNumber } from "../../forms/validators.js";
import FormBuilder from "../../forms/FormBuilder.js";

/**
 * Account line form for a manual entry journal.
 *
 * It renders a dropdown to pick an account, and 2 inputs fields
 * to set the debit/credit associated with the account. The 2 input
 * fields are mutually exclusive.
 *
 * @param {ManualJournalEntryTransaction} transaction - Transaction to render
 * @param {Account[]} [allAccounts] - List of accounts from which to choose
 * @param {boolean} [hideLabel] - When true, the input labels are not rendered
 * @return {FormBuilder}
 */
export default function makeTransactionFieldForm({
	transaction,
	allAccounts = [],
	hideLabel = false,
	onInputChange,
}) {
	const form = new FormBuilder({
		klass: "transaction-field-form",
		vertical: true,
		showActions: false,
		showCancel: false,
	});

	const inputs = {
		creditInput: undefined,
		debitInput: undefined,
	};

	appendAccountInput({
		form,
		transaction,
		allAccounts,
		hideLabel,
		onInputChange,
	});

	appendDebitInput({ form, transaction, hideLabel, inputs, onInputChange });

	appendCreditInput({
		form,
		transaction,
		hideLabel,
		inputs,
		onInputChange,
	});

	return form;
}

function appendAccountInput({
	form,
	hideLabel,
	allAccounts,
	transaction,
	onInputChange = () => {},
}) {
	form.newDropDownField({
		dataTestName: "account",
		label: hideLabel ? "" : _("Accounts"),
		items: allAccounts,
		noDefaultSelection: true,
		noValidation: true,
		noSelectionText: "",
		liveSearch: true,
		accessor: () => transaction.getAccount(),
		onChange: (value) => {
			transaction.setAccount(value);
			onInputChange();
		},
		themeClass: "accounts-container",
		class: "accounts",
	});
}

function appendCreditInput({
	form,
	hideLabel,
	transaction,
	inputs,
	onInputChange = () => {},
}) {
	inputs.creditInput = form.input({
		label: hideLabel ? "" : _("Credit"),
		name: "credit",
		validator: validNumber(),
		accessor: () => transaction.getCredit()?.toString() ?? "",
		disabled: transaction.getDebit() > 0,
		attributes: {
			change: ({ target: { value } }) => {
				if (value && +value > 0) {
					deactivateInput({
						input: inputs.debitInput,
					});
				} else {
					activateInput({
						input: inputs.debitInput,
					});
				}
				transaction.setValue(-value);
				onInputChange();
			},
			min: "0",
		},
	});
}

function appendDebitInput({
	form,
	hideLabel,
	transaction,
	inputs,
	onInputChange = () => {},
}) {
	inputs.debitInput = form.input({
		label: hideLabel ? "" : _("Debit"),
		name: "debit",
		accessor: () => transaction.getDebit()?.toString() ?? "",
		validator: validNumber(),
		disabled: transaction.getCredit() > 0,
		attributes: {
			change: ({ target: { value } }) => {
				if (value && +value > 0) {
					deactivateInput({
						input: inputs.creditInput,
					});
				} else {
					activateInput({
						input: inputs.creditInput,
					});
				}
				transaction.setValue(+value);
				onInputChange();
			},
			min: "0",
		},
	});
}

function activateInput({ input }) {
	input.enabled(true);
	input.setValue("0");
}

function deactivateInput({ input }) {
	input.enabled(false);
	input.setValue("");
}
